import { useCallback, useState } from 'react';
import { normalizeFilter, parseStoredViews, slugify, type ViewDef } from '../../lib/requestChips';

/**
 * Saved-view tabs over the /access list. A view is only a named filter string:
 * clicking one writes its `q` to the url, and the active tab is whichever view
 * the current url matches — so a pasted link lights up the right tab too.
 *
 * Personal views live in localStorage. Every access is wrapped: in a private
 * window, with storage blocked, or in a preview, the page keeps working and a
 * view you add lasts for the session only.
 */
const STORAGE_KEY = 'infras.access.views';

export interface PersonalView {
  name: string;
  q: string;
}

function readStored(key: string, normalize?: (q: string) => string): PersonalView[] {
  try {
    const v = parseStoredViews(window.localStorage.getItem(key));
    // parseStoredViews normalises with the /access language; another list re-normalises
    // the RAW stored q in its own (an unknown `kind:` word is free text to /access).
    if (!normalize) return v;
    const raw = JSON.parse(window.localStorage.getItem(key) ?? '[]') as { name?: string; q?: string }[];
    return v.map((x) => ({ ...x, q: normalize(raw.find((r) => r.name?.trim() === x.name)?.q ?? x.q) }));
  } catch {
    return [];
  }
}

function writeStored(key: string, v: PersonalView[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* storage unavailable — keep the in-memory copy */
  }
}

/** `storageKey` / `normalize` default to the /access list's; other lists pass their own. */
export function usePersonalViews(storageKey = STORAGE_KEY, normalize?: (q: string) => string) {
  const [views, setViews] = useState<PersonalView[]>(() => readStored(storageKey, normalize));
  const save = useCallback((name: string, q: string) => {
    setViews((prev) => {
      const next = [...prev.filter((v) => v.name !== name), { name, q: (normalize ?? normalizeFilter)(q) }].slice(-20);
      writeStored(storageKey, next);
      return next;
    });
  }, [storageKey, normalize]);
  const remove = useCallback((name: string) => {
    setViews((prev) => {
      const next = prev.filter((v) => v.name !== name);
      writeStored(storageKey, next);
      return next;
    });
  }, [storageKey]);
  return { views, save, remove };
}

export function SavedViews({
  presets,
  personal,
  q,
  count,
  onSelect,
  onSave,
  onRemove,
  adding,
  setAdding,
  normalize = normalizeFilter,
}: {
  presets: ViewDef[];
  personal: PersonalView[];
  q: string;
  /** null = no count shown (e.g. a personal view on a server-paged list). */
  count: (q: string) => number | null;
  onSelect: (q: string) => void;
  onSave: (name: string) => void;
  onRemove: (name: string) => void;
  adding: boolean;
  setAdding: (v: boolean) => void;
  /** Canonical form of a filter in this list's language; defaults to /access's. */
  normalize?: (q: string) => string;
}) {
  const cur = normalize(q);
  const [name, setName] = useState('');
  const tab = (key: string, testid: string, label: string, viewQ: string) => {
    const active = normalize(viewQ) === cur;
    const n = count(viewQ);
    return (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={active}
        className={active ? 'active' : undefined}
        data-testid={testid}
        onClick={() => onSelect(viewQ)}
      >
        {label} {n !== null && <span className="req-views__count">{n}</span>}
      </button>
    );
  };
  const submit = () => {
    const n = name.trim();
    if (n) onSave(n.slice(0, 40));
    setName('');
    setAdding(false);
  };
  return (
    <div className="tabs req-views" role="tablist" aria-label="Saved views">
      {presets.map((v) => tab(v.slug, `req-view-${v.slug}`, v.label, v.q))}
      {personal.map((v) => (
        <span key={`p-${v.name}`} className="req-views__custom">
          {tab(`p-${v.name}`, `req-view-custom-${slugify(v.name)}`, v.name, v.q)}
          <button
            type="button"
            className="req-views__del"
            aria-label={`Delete view ${v.name}`}
            title="Delete view"
            onClick={() => onRemove(v.name)}
          >
            ×
          </button>
        </span>
      ))}
      {adding ? (
        <span className="req-views__new">
          <input
            autoFocus
            aria-label="New view name"
            placeholder="View name"
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') {
                setName('');
                setAdding(false);
              }
            }}
          />
          <button type="button" className="mini-btn" onClick={submit} disabled={!name.trim()}>
            Save
          </button>
        </span>
      ) : (
        <button
          type="button"
          className="req-views__add"
          data-testid="req-view-add"
          title={cur ? 'Save the current filter as a view' : 'Add a filter first, then save it as a view'}
          disabled={!cur}
          onClick={() => setAdding(true)}
        >
          + Add view
        </button>
      )}
    </div>
  );
}
